/* eslint-disable import/no-duplicates */
import { Config } from '@chegg-wt/core/config'
import {
  CHANNEL_CONNECTED_EVENT,
  CHANNEL_SUBSCRIPTION_ERROR_EVENT,
  PUSHER_CONNECTED_EVENT,
  PUSHER_ERROR_EVENT,
  PUSHER_STATES,
  PUSHER_STATE_CHANGE_EVENT,
  PUSHER_STATE_DISCONNECTED,
  PUSHER_STATE_UNAVAILABLE,
  WTAI_EXPRESS_URL,
} from '@chegg-wt/core-wa/constants/pusher'
import type Pusher from 'pusher-js'
import type { Channel } from 'pusher-js'
import { getUserAndToken } from '@chegg-wt/core/api/user'
import {
  getLocalStorageItem,
  LOCAL_STORAGE_KEYS,
  updateUserAndToken,
} from '@chegg-wt/core/utils/localStorage'
import { isProduction } from '@chegg-wt/core/utils/env'

export interface PusherStateChange {
  previous: PUSHER_STATES
  current: PUSHER_STATES
}

export interface PusherData<T, K = string | null> {
  action: string
  traceId: string
  message: T | null
  error: K | null
}

/**
 * pusher-js client used to upload and check papers in CWC
 * `initialize()` the client before subscribing to a channel.
 * `unsubscribe()` to close the connection and event bindings
 */
export class PusherClient {
  public pusher?: Pusher

  public channel?: Channel

  /**
   * lazy-loads `pusher-js` and setups the connection
   */
  public initialize = ({
    appKey,
    cluster,
    onConnect,
    onDisconnect,
  }: {
    appKey: string
    cluster: string
    onConnect?: () => void
    onDisconnect?: () => void
  }): Promise<void> =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      const Pusher = (await import('pusher-js')).default

      if (this.pusher) {
        await this.unsubscribe()
      }

      this.pusher = new Pusher(appKey, {
        authEndpoint: `${Config.wtaiUrl}${WTAI_EXPRESS_URL}/auth`,
        auth: {
          headers: {
            'Cache-Control': 'no-store',
          },
        },
        cluster,
      })

      this.pusher.connection.bind(
        PUSHER_STATE_CHANGE_EVENT,
        (state: PusherStateChange) => {
          if (!isProduction())
            console.error(`State change from ${state.previous} to ${state.current}`)
          if (
            state.current === PUSHER_STATE_DISCONNECTED ||
            state.current === PUSHER_STATE_UNAVAILABLE
          ) {
            onDisconnect?.()
          }
        }
      )

      this.pusher.connection.bind(PUSHER_CONNECTED_EVENT, () => {
        onConnect?.()
        resolve()
      })

      this.pusher.connection.bind(PUSHER_ERROR_EVENT, (error: unknown) => {
        if (!isProduction()) console.error(`Pusher connection error`, error)
        reject(error)
      })
    })

  /**
   * subscribes to a channel to start receiving events
   */
  public subscribe = (channelName: string, retries: number = 2): Promise<void> =>
    new Promise((resolve, reject) => {
      if (this.pusher) {
        let retriesLeft = retries
        let updatedAuthToken = getLocalStorageItem(LOCAL_STORAGE_KEYS.uploadWidgetToken)

        const attempt = () => {
          this.pusher.options.auth.headers.Authorization = `Bearer ${updatedAuthToken}`

          this.channel = this.pusher.subscribe(channelName)

          this.channel.bind(CHANNEL_CONNECTED_EVENT, () => {
            resolve()
          })
          this.channel.bind(
            CHANNEL_SUBSCRIPTION_ERROR_EVENT,
            async ({
              type,
              error,
              status,
            }: {
              type: string
              error: string
              status: number
            }) => {
              if (!isProduction())
                console.error(
                  `Pusher channel connection error ${error} with status ${status}`
                )
              // For authentication errors, retry after fetching new token
              if (status === 401 || status === 403) {
                if (retriesLeft === 0) {
                  reject(new Error('Subscription failed and all retries exhausted'))
                } else {
                  retriesLeft -= 1
                  const userAndToken = await getUserAndToken()
                  updateUserAndToken(userAndToken)
                  updatedAuthToken = userAndToken.jwt
                  attempt()
                }
              } else {
                reject(
                  new Error(
                    `Subscription failed with type [${type}] status ${status}: ${error}`
                  )
                )
              }
            }
          )
        }

        attempt()
      } else {
        reject(new Error(`Unable to subscribe to channel ${channelName}`))
      }
    })

  /**
   * unsubscribes from a channel and closes the pusher connection
   */
  public unsubscribe = (channelName?: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (this.pusher) {
        this.pusher.disconnect()
        this.pusher.unbind_all()
        if (this.channel && channelName) {
          this.pusher.unsubscribe(channelName)
        }
        this.channel = undefined
        resolve()
      } else {
        reject()
      }
    })
}

export default PusherClient
